import { useMutation, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { AnimatePresence, motion } from "framer-motion"
import { Check, Plus, Search, X } from "lucide-react"
import { useRef, useState } from "react"
import { toast } from "sonner"
import { createTaxonomy, type TaxonomyKind } from "@/entities/category"
import { TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Skeleton } from "@/shared/ui/skeleton"

interface Item {
	id: string
	name: string
	iconUrl?: string | null
	postCount?: number
}

function Icon({ url }: { url?: string | null }) {
	if (!url) return null
	return <img src={url} alt="" className="size-4 shrink-0 rounded object-cover" />
}

/** Строка классификации: подпись слева, справа выбранное и кнопка выбора. */
export function TaxonomyRow({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="grid gap-2 border-b border-border/70 py-3.5 first:pt-0 last:border-0 last:pb-0 sm:grid-cols-[120px_minmax(0,1fr)] sm:items-start sm:gap-4">
			<span className="pt-1.5 text-sm font-medium text-muted-foreground">{label}</span>
			<div className="min-w-0">{children}</div>
		</div>
	)
}

/**
 * Несколько значений из длинного справочника. Выбранное видно чипами, остальное
 * ищется в выпадающем списке, где заодно видно, сколько материалов уже с меткой,
 * и можно завести новую, если подходящей нет.
 */
export function TaxonomyPicker({
	label,
	kind,
	items,
	value,
	onChange,
	loading = false,
}: {
	label: string
	kind: TaxonomyKind
	items: Item[] | undefined
	value: string[]
	onChange: (value: string[]) => void
	loading?: boolean
}) {
	const queryClient = useQueryClient()
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState("")
	const [cursor, setCursor] = useState(0)
	const list = useRef<HTMLUListElement>(null)
	const all = items ?? []
	const selected = value
		.map((id) => all.find((item) => item.id === id))
		.filter((item): item is Item => !!item)
	const needle = query.trim().toLocaleLowerCase()
	const shown = all
		.filter((item) => !needle || item.name.toLocaleLowerCase().includes(needle))
		.sort((left, right) => (right.postCount ?? 0) - (left.postCount ?? 0))
	const exact = all.some((item) => item.name.toLocaleLowerCase() === needle)

	const toggle = (id: string) =>
		onChange(value.includes(id) ? value.filter((item) => item !== id) : [...value, id])

	const create = useMutation({
		mutationFn: () => createTaxonomy(kind, query.trim()),
		onSuccess: async (item) => {
			await queryClient.invalidateQueries({ queryKey: [kind] })
			onChange([...value, item.id])
			setQuery("")
			toast.success(`Добавлено: ${item.name}`)
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось добавить")),
	})

	// Один LoadSwap на обе ветки: заглушка гаснет поверх, метки проявляются на её месте.
	return (
		<LoadSwap loading={loading}>
			{loading ? (
				<Skeleton className="h-8 w-40 rounded-full" />
			) : (
				<div className="flex flex-wrap items-center gap-1.5">
					<AnimatePresence initial={false}>
						{selected.map((item) => (
							<motion.span
								key={item.id}
								layout
								initial={{ opacity: 0, scale: 0.9 }}
								animate={{ opacity: 1, scale: 1 }}
								exit={{ opacity: 0, scale: 0.9 }}
								transition={TRANSITION.fast}
								className="inline-flex h-8 items-center gap-1.5 rounded-full bg-secondary pr-1 pl-3 text-sm"
							>
								<Icon url={item.iconUrl} />
								{item.name}
								<button
									type="button"
									onClick={() => toggle(item.id)}
									aria-label={`Убрать «${item.name}»`}
									className="grid size-6 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-background/60 hover:text-foreground"
								>
									<X className="size-3.5" />
								</button>
							</motion.span>
						))}
					</AnimatePresence>
					<Popover
						open={open}
						onOpenChange={(next) => {
							setOpen(next)
							if (!next) setQuery("")
							setCursor(0)
						}}
					>
						<PopoverTrigger asChild>
							<button
								type="button"
								className="inline-flex h-8 items-center gap-1 rounded-full border border-dashed border-border px-3 text-sm text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
							>
								<Plus className="size-3.5" />
								{selected.length ? "Ещё" : `Выбрать ${label.toLowerCase()}`}
							</button>
						</PopoverTrigger>
						<PopoverContent align="start" className="w-80 overflow-hidden rounded-xl p-0">
							<div className="relative border-b border-border">
								<Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
								<input
									autoFocus
									value={query}
									onChange={(event) => {
										setQuery(event.target.value)
										setCursor(0)
									}}
									onKeyDown={(event) => {
										if (event.key === "ArrowDown") {
											event.preventDefault()
											setCursor((index) => Math.min(shown.length - 1, index + 1))
										} else if (event.key === "ArrowUp") {
											event.preventDefault()
											setCursor((index) => Math.max(0, index - 1))
										} else if (event.key === "Enter") {
											event.preventDefault()
											const item = shown[cursor]
											if (item) toggle(item.id)
											else if (needle && !exact) create.mutate()
										}
									}}
									placeholder={`Найти среди ${all.length}`}
									aria-label={`Поиск: ${label}`}
									className="h-11 w-full bg-transparent pr-3 pl-9 text-sm outline-none placeholder:text-muted-foreground"
								/>
							</div>
							<ul
								ref={list}
								className="max-h-72 overflow-y-auto p-1"
								role="listbox"
								aria-multiselectable
							>
								{shown.map((item, index) => {
									const active = value.includes(item.id)
									return (
										<li key={item.id} role="option" aria-selected={active}>
											<button
												type="button"
												onMouseEnter={() => setCursor(index)}
												onClick={() => toggle(item.id)}
												className={cn(
													"flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm",
													cursor === index && "bg-accent",
												)}
											>
												<span
													className={cn(
														"grid size-4 shrink-0 place-items-center rounded border transition-colors",
														active
															? "border-primary bg-primary text-primary-foreground"
															: "border-muted-foreground/40",
													)}
												>
													{active ? <Check className="size-3" strokeWidth={3} /> : null}
												</span>
												<Icon url={item.iconUrl} />
												<span className="min-w-0 flex-1 truncate">{item.name}</span>
												{item.postCount !== undefined ? (
													<span className="text-xs text-muted-foreground tabular-nums">
														{item.postCount}
													</span>
												) : null}
											</button>
										</li>
									)
								})}
								{needle && !exact ? (
									<li>
										<button
											type="button"
											disabled={create.isPending}
											onClick={() => create.mutate()}
											className={cn(
												"flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-primary",
												shown.length === 0 && "bg-accent",
											)}
										>
											<Plus className="size-4" />
											Создать «{query.trim()}»
										</button>
									</li>
								) : null}
								{!needle && shown.length === 0 ? (
									<li className="px-2.5 py-3 text-sm text-muted-foreground">Справочник пуст</li>
								) : null}
							</ul>
						</PopoverContent>
					</Popover>
				</div>
			)}
		</LoadSwap>
	)
}

/** Одно значение из короткого справочника: сегменты в одну строку. */
export function TaxonomySegments({
	items,
	value,
	onChange,
	loading = false,
}: {
	items: Item[] | undefined
	value: string[]
	onChange: (value: string[]) => void
	loading?: boolean
}) {
	return (
		<LoadSwap loading={loading}>
			{loading ? (
				<Skeleton className="h-9 w-72 rounded-xl" />
			) : (
				<SegmentedControl
					options={(items ?? []).map((item) => ({ value: item.id, label: item.name }))}
					value={value[0]}
					allowEmpty
					onChange={(next) => onChange(next ? [next] : [])}
				/>
			)}
		</LoadSwap>
	)
}
